import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { Player } from '../entities/Player.js';
import { Enemy } from '../entities/Enemy.js';
import { Ally } from '../entities/Ally.js';
import { audioManager } from '../audio/AudioManager.js';
import { AStarPathfinding } from '../ai/Pathfinding.js';
import { BloodParticleSystem } from '../effects/BloodParticles.js';
import { ConfettiSystem } from '../effects/Confetti.js';
import { NetworkManager } from '../network/NetworkManager.js';

export class Game {
    constructor(canvas, options = {}) {
        this.canvas = canvas;
        this.options = options;
        this.scene = new THREE.Scene();
        this.camera = null;
        this.renderer = null;
        this.player = null;
        this.enemies = [];
        this.allies = [];
        this.clock = new THREE.Clock();
        this.isRunning = false;
        
        // Difficulty settings
        const difficulty = options.difficulty || 'medium';
        this.setDifficulty(difficulty);
        
        this.enemySpawnTimer = 0;
        
        // Physics world
        this.world = new CANNON.World({
            gravity: new CANNON.Vec3(0, -9.82, 0),
            allowSleep: false
        });
        
        // Create contact materials for better collision handling
        const defaultMaterial = new CANNON.Material('default');
        const playerMaterial = new CANNON.Material('player');
        const obstacleMaterial = new CANNON.Material('obstacle');
        
        // Player-Obstacle contact
        const playerObstacleContact = new CANNON.ContactMaterial(playerMaterial, obstacleMaterial, {
            friction: 0.1,
            restitution: 0.0 // No bouncing
        });
        this.world.addContactMaterial(playerObstacleContact);
        
        // Store materials for use in obstacles
        this.playerMaterial = playerMaterial;
        this.obstacleMaterial = obstacleMaterial;
        
        // Pathfinding
        this.pathfinding = new AStarPathfinding(100, 100, 1);
        
        // Network manager (for co-op) - only initialize if needed
        this.networkManager = null; // Will be initialized in initGame if needed
        
        // Blood particle system
        this.bloodSystem = new BloodParticleSystem(this.scene);
        
        // Confetti system
        this.confettiSystem = new ConfettiSystem(this.scene);
        
        // Wave system
        this.currentWave = 1;
        this.enemiesInWave = 0;
        this.enemiesKilledInWave = 0;
        this.waveComplete = false;
        
        // Callbacks
        this.onHealthChange = options.onHealthChange || (() => {});
        this.onAmmoChange = options.onAmmoChange || (() => {});
        this.onKill = options.onKill || (() => {});
        this.onReloadChange = options.onReloadChange || (() => {});
        this.onDeath = options.onDeath || (() => {});
        this.onWaveComplete = options.onWaveComplete || (() => {});
        this.onWaveChange = options.onWaveChange || (() => {});
        
        this.init();
    }
    
    setDifficulty(difficulty) {
        switch(difficulty) {
            case 'easy':
                this.minEnemies = 3;
                this.maxEnemies = 8;
                this.enemySpawnInterval = 8.0; // Much slower spawn
                this.difficultyMultiplier = 0.5; // 50% less damage/health
                this.initialEnemyCount = 3;
                this.allyCount = 10; // More allies to help
                this.enemiesPerWave = 5; // Much fewer enemies per wave
                break;
            case 'hard':
                this.minEnemies = 25;
                this.maxEnemies = 40;
                this.enemySpawnInterval = 2.0;
                this.difficultyMultiplier = 1.5; // 50% more damage/health
                this.initialEnemyCount = 25;
                this.allyCount = 3;
                this.enemiesPerWave = 30; // Base enemies per wave
                break;
            case 'medium':
            default:
                this.minEnemies = 15;
                this.maxEnemies = 30;
                this.enemySpawnInterval = 3.0;
                this.difficultyMultiplier = 1.0; // Normal
                this.initialEnemyCount = 15;
                this.allyCount = 5;
                this.enemiesPerWave = 20; // Base enemies per wave
                break;
            case 'impossible':
                this.minEnemies = 40;
                this.maxEnemies = 60;
                this.enemySpawnInterval = 1.0; // Very fast spawn
                this.difficultyMultiplier = 2.0; // Double damage/health
                this.initialEnemyCount = 40;
                this.allyCount = 1; // Almost no allies
                this.enemiesPerWave = 50; // Many enemies per wave
                this.waveScaling = 0.5; // 50% more enemies per wave
                break;
        }
    }
    
    /**
     * Calculate enemies for current wave
     * Each wave increases the number of enemies
     */
    getEnemiesForWave(wave) {
        // Base enemies + scaling per wave
        // Much slower scaling for easier difficulty
        const difficulty = this.options.difficulty || 'medium';
        let scaleFactor = 0.2; // Default
        if (difficulty === 'easy') {
            scaleFactor = 0.1; // Very slow scaling
        } else if (difficulty === 'hard') {
            scaleFactor = 0.3; // Faster scaling
        }
        const multiplier = 1 + (wave - 1) * scaleFactor;
        return Math.floor(this.enemiesPerWave * multiplier);
    }
    
    /**
     * Start a new wave
     */
    startWave(wave) {
        this.currentWave = wave;
        this.waveComplete = false;
        this.enemiesKilledInWave = 0;
        const enemiesToSpawn = this.getEnemiesForWave(wave);
        this.enemiesInWave = enemiesToSpawn;
        
        // Spawn all enemies for this wave
        this.spawnEnemies(enemiesToSpawn);
        
        // Notify UI
        this.onWaveChange?.(wave, enemiesToSpawn);
    }
    
    /**
     * Check if wave is complete
     */
    checkWaveComplete() {
        // Wave is complete when all enemies are killed
        if (this.enemies.length === 0 && this.enemiesKilledInWave >= this.enemiesInWave && !this.waveComplete) {
            this.waveComplete = true;
            this.completeWave();
        }
    }
    
    /**
     * Complete current wave
     */
    completeWave() {
        // Create confetti at player position
        if (this.player && this.player.camera) {
            const playerPos = this.player.camera.position.clone();
            playerPos.y += 2; // Above player
            this.confettiSystem.createConfetti(playerPos, 150);
        }
        
        // Notify UI
        this.onWaveComplete?.(this.currentWave);
        
        // Check if player won (10 waves completed)
        if (this.currentWave >= 10) {
            this.onGameWin?.();
            return; // Don't start next wave
        }
        
        // Start next wave after a delay
        setTimeout(() => {
            this.startWave(this.currentWave + 1);
        }, 3000); // 3 second delay before next wave
    }
    
    init() {
        // Setup renderer
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        
        // Setup camera
        this.camera = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );
        this.camera.position.set(0, 1.6, 0);
        
        // Setup lighting
        const ambientLight = new THREE.AmbientLight(0x404040, 0.5);
        this.scene.add(ambientLight);
        
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(10, 10, 5);
        directionalLight.castShadow = true;
        directionalLight.shadow.mapSize.width = 2048;
        directionalLight.shadow.mapSize.height = 2048;
        directionalLight.shadow.camera.near = 0.5;
        directionalLight.shadow.camera.far = 50;
        directionalLight.shadow.camera.left = -20;
        directionalLight.shadow.camera.right = 20;
        directionalLight.shadow.camera.top = 20;
        directionalLight.shadow.camera.bottom = -20;
        this.scene.add(directionalLight);
        
        // Create ground
        this.createGround();
        
        // Add obstacles - TEMPORARILY DISABLED
        // this.createObstacles();
        
        // Initialize audio
        audioManager.preloadCommonSounds();
        
        // Handle window resize
        window.addEventListener('resize', () => this.onWindowResize());
        
        // Pointer lock for FPS controls
        this.canvas.addEventListener('click', () => {
            if (this.isRunning) {
                this.canvas.requestPointerLock();
            }
        });
    }
    
    createGround() {
        // Ground boundaries (player cannot leave this area)
        this.groundSize = 100;
        this.groundBoundary = this.groundSize / 2 - 2; // 2 units margin from edge
        
        // Three.js ground with texture variation
        const groundGeometry = new THREE.PlaneGeometry(this.groundSize, this.groundSize, 20, 20);
        
        // Create varied ground material
        const groundMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x2d5016,
            roughness: 0.8,
            metalness: 0.1
        });
        
        const ground = new THREE.Mesh(groundGeometry, groundMaterial);
        ground.rotation.x = -Math.PI / 2;
        ground.receiveShadow = true;
        this.scene.add(ground);
        
        // Add some terrain variation (heuveltjes)
        this.createTerrainVariation();
        
        // Cannon.js physics ground
        const groundShape = new CANNON.Plane();
        const groundBody = new CANNON.Body({ mass: 0 });
        groundBody.addShape(groundShape);
        groundBody.quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI / 2);
        this.world.addBody(groundBody);
    }
    
    createTerrainVariation() {
        // Add hills (heuveltjes) - small elevation changes
        for (let i = 0; i < 15; i++) {
            const hillSize = Math.random() * 8 + 4;
            const hillGeometry = new THREE.ConeGeometry(hillSize, Math.random() * 2 + 1, 8);
            const hillMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x3a6b1f,
                roughness: 0.9
            });
            const hill = new THREE.Mesh(hillGeometry, hillMaterial);
            
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * 35;
            hill.position.set(
                Math.cos(angle) * distance,
                0.5,
                Math.sin(angle) * distance
            );
            hill.rotation.x = -Math.PI / 2;
            hill.receiveShadow = true;
            hill.castShadow = true;
            hill.userData.isObstacle = true;
            this.scene.add(hill);
            
            // Physics body for hill
            const hillHeight = Math.random() * 2 + 1;
            const hillShape = new CANNON.Cylinder(hillSize, hillSize, hillHeight, 8);
            const hillBody = new CANNON.Body({ mass: 0 });
            hillBody.addShape(hillShape);
            hillBody.position.set(hill.position.x, hillHeight / 2, hill.position.z);
            hillBody.quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI / 2);
            hillBody.material = this.obstacleMaterial || new CANNON.Material('obstacle');
            this.world.addBody(hillBody);
        }
    }
    
    createObstacles() {
        // Add trees
        this.createTrees();
        
        // Add rocks
        this.createRocks();
        
        // Add some boxes as cover/obstacles
        for (let i = 0; i < 8; i++) {
            const size = Math.random() * 2 + 1;
            const boxGeometry = new THREE.BoxGeometry(size, size, size);
            const boxMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x654321,
                roughness: 0.9
            });
            const box = new THREE.Mesh(boxGeometry, boxMaterial);
            box.position.set(
                (Math.random() - 0.5) * 40,
                size / 2,
                (Math.random() - 0.5) * 40
            );
            box.castShadow = true;
            box.receiveShadow = true;
            box.userData.isObstacle = true;
            this.scene.add(box);
            
            // Physics body
            const boxShape = new CANNON.Box(new CANNON.Vec3(size/2, size/2, size/2));
            const boxBody = new CANNON.Body({ mass: 0 });
            boxBody.addShape(boxShape);
            boxBody.position.set(box.position.x, box.position.y, box.position.z);
            boxBody.material = this.obstacleMaterial || new CANNON.Material('obstacle');
            this.world.addBody(boxBody);
        }
        
        // Update pathfinding grid
        this.pathfinding.updateObstaclesFromScene(this.scene);
    }
    
    createTrees() {
        // Create trees with trunk and foliage
        for (let i = 0; i < 25; i++) {
            const treeGroup = new THREE.Group();
            
            // Trunk
            const trunkHeight = Math.random() * 3 + 2;
            const trunkRadius = 0.3 + Math.random() * 0.2;
            const trunkGeometry = new THREE.CylinderGeometry(trunkRadius, trunkRadius * 1.2, trunkHeight, 8);
            const trunkMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x4a2c1a,
                roughness: 0.9
            });
            const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
            trunk.position.y = trunkHeight / 2;
            trunk.castShadow = true;
            trunk.receiveShadow = true;
            treeGroup.add(trunk);
            
            // Foliage (leaves)
            const foliageSize = Math.random() * 2 + 2;
            const foliageGeometry = new THREE.ConeGeometry(foliageSize, foliageSize * 1.2, 8);
            const foliageMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x1a5c1a,
                roughness: 0.8
            });
            const foliage = new THREE.Mesh(foliageGeometry, foliageMaterial);
            foliage.position.y = trunkHeight + foliageSize * 0.3;
            foliage.castShadow = true;
            foliage.receiveShadow = true;
            treeGroup.add(foliage);
            
            // Random position
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * 40;
            treeGroup.position.set(
                Math.cos(angle) * distance,
                0,
                Math.sin(angle) * distance
            );
            
            treeGroup.userData.isObstacle = true;
            this.scene.add(treeGroup);
            
            // Physics body for trunk
            const trunkShape = new CANNON.Cylinder(trunkRadius, trunkRadius * 1.2, trunkHeight, 8);
            const trunkBody = new CANNON.Body({ mass: 0 });
            trunkBody.addShape(trunkShape);
            trunkBody.position.set(treeGroup.position.x, trunkHeight / 2, treeGroup.position.z);
            trunkBody.material = this.obstacleMaterial || new CANNON.Material('obstacle');
            this.world.addBody(trunkBody);
        }
    }
    
    createRocks() {
        // Create rocks of various sizes
        for (let i = 0; i < 20; i++) {
            const rockSize = Math.random() * 1.5 + 0.5;
            const rockGeometry = new THREE.DodecahedronGeometry(rockSize, 0);
            const rockMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x555555,
                roughness: 0.9,
                metalness: 0.1
            });
            const rock = new THREE.Mesh(rockGeometry, rockMaterial);
            
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * 40;
            rock.position.set(
                Math.cos(angle) * distance,
                rockSize / 2,
                Math.sin(angle) * distance
            );
            
            // Random rotation
            rock.rotation.set(
                Math.random() * Math.PI,
                Math.random() * Math.PI,
                Math.random() * Math.PI
            );
            
            rock.castShadow = true;
            rock.receiveShadow = true;
            rock.userData.isObstacle = true;
            this.scene.add(rock);
            
            // Physics body
            const rockShape = new CANNON.Box(new CANNON.Vec3(rockSize, rockSize, rockSize));
            const rockBody = new CANNON.Body({ mass: 0 });
            rockBody.addShape(rockShape);
            rockBody.position.set(rock.position.x, rock.position.y, rock.position.z);
            rockBody.material = this.obstacleMaterial || new CANNON.Material('obstacle');
            this.world.addBody(rockBody);
        }
    }
    
    initGame() {
        // Initialize game entities but don't start yet
        const race = this.options.race || 'human';
        const mode = this.options.mode || 'single';
        
        // Connect to network if co-op
        if (mode === 'coop') {
            if (!this.networkManager) {
                this.networkManager = new NetworkManager(this);
            }
            this.networkManager.connect('http://localhost:3000', race, mode);
        }
        
        // Create player with difficulty adjustments
        const difficulty = this.options.difficulty || 'medium';
        const playerHealthMultiplier = difficulty === 'easy' ? 1.5 : (difficulty === 'hard' ? 0.8 : 1.0); // More health on easy
        
        this.player = new Player(
            this.scene, 
            this.camera, 
            this.world,
            race,
            {
                onHealthChange: this.onHealthChange,
                onAmmoChange: this.onAmmoChange,
                onReloadChange: this.onReloadChange,
                onDeath: this.onDeath,
                getEnemies: () => this.enemies,
                healthMultiplier: playerHealthMultiplier
            }
        );
        
        // Create allies (same race as player, based on difficulty)
        this.spawnAllies(this.allyCount);
        
        // Start first wave
        this.startWave(1);
        
        // Start rendering loop (but game is not active yet)
        this.animate();
    }
    
    start() {
        // Actually start the game - enable gameplay
        this.isRunning = true;
        
        // Play background music (if available)
        // audioManager.playMusic('/sounds/background_music.mp3', { volume: 0.3 });
    }
    
    // Helper function to check if a position is clear of obstacles
    isPositionClear(x, z, minDistanceFromObstacle = 2.0) {
        const checkPosition = new THREE.Vector3(x, 0, z);
        let isClear = true;
        
        // Check all obstacles in the scene
        this.scene.traverse((object) => {
            if (!isClear) return; // Early exit if already found collision
            
            if (object.userData.isObstacle === true || 
                (object.isMesh && object.parent && object.parent.userData && object.parent.userData.isObstacle === true)) {
                
                // Get the obstacle position
                const obstaclePos = new THREE.Vector3();
                if (object.parent && object.parent.userData && object.parent.userData.isObstacle === true) {
                    object.parent.getWorldPosition(obstaclePos);
                } else {
                    object.getWorldPosition(obstaclePos);
                }
                
                // Calculate horizontal distance (ignore Y)
                const dx = checkPosition.x - obstaclePos.x;
                const dz = checkPosition.z - obstaclePos.z;
                const distance = Math.sqrt(dx * dx + dz * dz);
                
                // Get obstacle size (approximate)
                let obstacleRadius = 1.5; // Default radius
                if (object.geometry) {
                    if (object.geometry.boundingBox) {
                        object.geometry.computeBoundingBox();
                        const size = object.geometry.boundingBox.getSize(new THREE.Vector3());
                        obstacleRadius = Math.max(size.x, size.z) / 2;
                    } else if (object.geometry.parameters) {
                        // For primitives with parameters
                        if (object.geometry.parameters.radius) {
                            obstacleRadius = object.geometry.parameters.radius;
                        } else if (object.geometry.parameters.width && object.geometry.parameters.height) {
                            obstacleRadius = Math.max(object.geometry.parameters.width, object.geometry.parameters.height) / 2;
                        } else if (object.geometry.parameters.radiusTop || object.geometry.parameters.radiusBottom) {
                            obstacleRadius = Math.max(
                                object.geometry.parameters.radiusTop || 0.5,
                                object.geometry.parameters.radiusBottom || 0.5
                            );
                        }
                    }
                }
                
                // Check if too close to obstacle
                if (distance < obstacleRadius + minDistanceFromObstacle) {
                    isClear = false;
                }
            }
        });
        
        return isClear;
    }
    
    spawnEnemies(count) {
        if (!this.player) return; // Can't spawn enemies without a player
        
        const mapSize = 45; // Half of the 100x100 map
        const minDistanceFromPlayer = 15; // Minimum distance from player
        const minDistanceBetweenEnemies = 5; // Minimum distance between enemies
        const minDistanceFromObstacle = 2.5; // Minimum distance from obstacles
        
        // Available enemy races (Wizard is excluded - only playable, not enemy)
        const availableEnemyRaces = ['human', 'orc', 'elf', 'troll'];
        
        for (let i = 0; i < count; i++) {
            const enemyRace = availableEnemyRaces[
                Math.floor(Math.random() * availableEnemyRaces.length)
            ];
            const enemy = new Enemy(
                this.scene,
                this.world,
                enemyRace,
                this.player,
                this.pathfinding,
                {
                    onDeath: () => {
                        this.onKill();
                        this.enemiesKilledInWave++;
                        this.checkWaveComplete(); // Check if wave is complete
                        audioManager.playSound('death_scream');
                    },
                    getAllies: () => this.allies, // Give enemies access to allies
                    difficultyMultiplier: this.difficultyMultiplier || 1.0, // Pass difficulty multiplier
                    groundBoundary: this.groundBoundary || 48, // Pass ground boundary
                    difficulty: this.options.difficulty || 'medium' // Pass difficulty for attack cooldown
                }
            );
            
            // Find a good spawn position
            const playerPos = this.player.camera.position;
            let spawnX, spawnZ;
            let attempts = 0;
            let validPosition = false;
            
            while (!validPosition && attempts < 50) {
                // Spawn enemies spread across the entire map
                // Use different spawn patterns for better distribution
                if (i % 4 === 0) {
                    // Spawn in corners
                    const corner = Math.floor(Math.random() * 4);
                    switch(corner) {
                        case 0: spawnX = -mapSize + Math.random() * 10; spawnZ = -mapSize + Math.random() * 10; break;
                        case 1: spawnX = mapSize - Math.random() * 10; spawnZ = -mapSize + Math.random() * 10; break;
                        case 2: spawnX = -mapSize + Math.random() * 10; spawnZ = mapSize - Math.random() * 10; break;
                        case 3: spawnX = mapSize - Math.random() * 10; spawnZ = mapSize - Math.random() * 10; break;
                    }
                } else if (i % 4 === 1) {
                    // Spawn along edges
                    const edge = Math.floor(Math.random() * 4);
                    switch(edge) {
                        case 0: spawnX = -mapSize + Math.random() * (mapSize * 2); spawnZ = -mapSize; break;
                        case 1: spawnX = mapSize; spawnZ = -mapSize + Math.random() * (mapSize * 2); break;
                        case 2: spawnX = -mapSize + Math.random() * (mapSize * 2); spawnZ = mapSize; break;
                        case 3: spawnX = -mapSize; spawnZ = -mapSize + Math.random() * (mapSize * 2); break;
                    }
                } else {
                    // Spawn randomly across the map
                    spawnX = (Math.random() - 0.5) * (mapSize * 2);
                    spawnZ = (Math.random() - 0.5) * (mapSize * 2);
                }
                
                // Check distance from player
                const distanceFromPlayer = Math.sqrt(
                    Math.pow(spawnX - playerPos.x, 2) + 
                    Math.pow(spawnZ - playerPos.z, 2)
                );
                
                if (distanceFromPlayer >= minDistanceFromPlayer) {
                    // Check distance from other enemies
                    let tooClose = false;
                    for (const existingEnemy of this.enemies) {
                        const distanceFromEnemy = Math.sqrt(
                            Math.pow(spawnX - existingEnemy.position.x, 2) + 
                            Math.pow(spawnZ - existingEnemy.position.z, 2)
                        );
                        if (distanceFromEnemy < minDistanceBetweenEnemies) {
                            tooClose = true;
                            break;
                        }
                    }
                    
                    // Check if position is clear of obstacles
                    if (!tooClose && this.isPositionClear(spawnX, spawnZ, minDistanceFromObstacle)) {
                        validPosition = true;
                    }
                }
                
                attempts++;
            }
            
            // If we couldn't find a valid position, try to find one near player but clear of obstacles
            if (!validPosition) {
                let fallbackAttempts = 0;
                while (!validPosition && fallbackAttempts < 30) {
                    const angle = Math.random() * Math.PI * 2;
                    const distance = minDistanceFromPlayer + Math.random() * 10;
                    spawnX = playerPos.x + Math.cos(angle) * distance;
                    spawnZ = playerPos.z + Math.sin(angle) * distance;
                    
                    if (this.isPositionClear(spawnX, spawnZ, minDistanceFromObstacle)) {
                        validPosition = true;
                    }
                    fallbackAttempts++;
                }
                
                // Last resort: spawn at player position (shouldn't happen often)
                if (!validPosition) {
                    spawnX = playerPos.x + (Math.random() - 0.5) * 5;
                    spawnZ = playerPos.z + (Math.random() - 0.5) * 5;
                }
            }
            
            // Validate positions to prevent NaN
            if (isNaN(spawnX) || isNaN(spawnZ)) {
                console.warn('Invalid spawn position for enemy, using fallback');
                spawnX = (Math.random() - 0.5) * 20;
                spawnZ = (Math.random() - 0.5) * 20;
            }
            
            // Clamp to map boundaries
            spawnX = Math.max(-mapSize, Math.min(mapSize, spawnX));
            spawnZ = Math.max(-mapSize, Math.min(mapSize, spawnZ));
            
            enemy.position.set(spawnX, 1, spawnZ);
            this.enemies.push(enemy);
        }
    }
    
    spawnAllies(count) {
        if (!this.player) return;
        
        const playerRace = this.options.race || 'human';
        console.log('Spawning allies for player race:', playerRace);
        const mapSize = 45;
        const minDistanceFromPlayer = 3; // Spawn near player
        const maxDistanceFromPlayer = 8;
        
        // Elven en Mensen werken samen
        // - Als speler mens is, spawn elven als allies
        // - Als speler elf is, spawn mensen als allies
        // - Als speler wizard is, spawn mensen en elven als allies (maar GEEN wizard - jij bent de enige wizard)
        const allyRaces = [];
        if (playerRace === 'human' || playerRace === 'elf') {
            // Spawn mix van mensen en elven
            const humanCount = Math.ceil(count / 2);
            const elfCount = count - humanCount;
            for (let i = 0; i < humanCount; i++) {
                if (playerRace !== 'human') allyRaces.push('human');
            }
            for (let i = 0; i < elfCount; i++) {
                if (playerRace !== 'elf') allyRaces.push('elf');
            }
            
            // Als er nog ruimte is, vul aan met beschikbare rassen
            while (allyRaces.length < count) {
                if (playerRace !== 'human') allyRaces.push('human');
                else if (playerRace !== 'elf') allyRaces.push('elf');
                else break;
            }
            
            // Trim to exact count
            allyRaces.length = count;
        } else if (playerRace === 'wizard') {
            // Wizard krijgt alleen mensen en elven als allies (geen andere wizards)
            const humanCount = Math.ceil(count / 2);
            const elfCount = count - humanCount;
            for (let i = 0; i < humanCount; i++) allyRaces.push('human');
            for (let i = 0; i < elfCount; i++) allyRaces.push('elf');
        } else {
            // Andere rassen (Orc, Troll) krijgen alleen hun eigen ras als allies
            for (let i = 0; i < count; i++) {
                allyRaces.push(playerRace);
            }
        }
        
        console.log('Ally races to spawn:', allyRaces);
        
        const minDistanceFromObstacle = 2.0; // Minimum distance from obstacles for allies
        
        for (let i = 0; i < allyRaces.length; i++) {
            const allyRace = allyRaces[i];
            const ally = new Ally(
                this.scene,
                this.world,
                allyRace,
                this.player,
                this.pathfinding,
                this.enemies, // Reference to enemies array
                {
                    onDeath: () => {
                        // Ally died
                        console.log('Ally died');
                    },
                    groundBoundary: this.groundBoundary || 48 // Pass ground boundary
                }
            );
            
            // Spawn allies near player, but on open ground
            const playerPos = this.player.camera.position;
            let spawnX, spawnZ;
            let attempts = 0;
            let validPosition = false;
            
            while (!validPosition && attempts < 30) {
                const angle = (i / allyRaces.length) * Math.PI * 2 + (Math.random() - 0.5) * 0.5; // Add some variation
                const distance = minDistanceFromPlayer + Math.random() * (maxDistanceFromPlayer - minDistanceFromPlayer);
                
                spawnX = playerPos.x + Math.cos(angle) * distance;
                spawnZ = playerPos.z + Math.sin(angle) * distance;
                
                // Check if position is clear of obstacles
                if (this.isPositionClear(spawnX, spawnZ, minDistanceFromObstacle)) {
                    validPosition = true;
                }
                
                attempts++;
            }
            
            // If we couldn't find a clear position, try a different angle
            if (!validPosition) {
                for (let j = 0; j < 20; j++) {
                    const angle = Math.random() * Math.PI * 2;
                    const distance = minDistanceFromPlayer + Math.random() * (maxDistanceFromPlayer - minDistanceFromPlayer);
                    spawnX = playerPos.x + Math.cos(angle) * distance;
                    spawnZ = playerPos.z + Math.sin(angle) * distance;
                    
                    if (this.isPositionClear(spawnX, spawnZ, minDistanceFromObstacle)) {
                        validPosition = true;
                        break;
                    }
                }
            }
            
            // Last resort: spawn close to player
            if (!validPosition) {
                spawnX = playerPos.x + (Math.random() - 0.5) * 3;
                spawnZ = playerPos.z + (Math.random() - 0.5) * 3;
            }
            
            // Validate positions to prevent NaN
            if (isNaN(spawnX) || isNaN(spawnZ)) {
                console.warn('Invalid spawn position for ally, using player position');
                spawnX = playerPos.x || 0;
                spawnZ = playerPos.z || 0;
            }
            
            // Clamp to map boundaries
            spawnX = Math.max(-mapSize, Math.min(mapSize, spawnX));
            spawnZ = Math.max(-mapSize, Math.min(mapSize, spawnZ));
            
            ally.position.set(spawnX, 1, spawnZ);
            this.allies.push(ally);
        }
    }
    
    animate() {
        requestAnimationFrame(() => this.animate());
        
        const deltaTime = Math.min(this.clock.getDelta(), 0.1); // Cap delta time
        
        // Always update physics and render (even during countdown)
        this.world.step(1/60, deltaTime, 3);
        
        // Only update gameplay if game is running and player is alive
        if (this.isRunning && this.player && this.player.health > 0) {
            // Update player
            this.player.update(deltaTime);
            
            // Update enemies - dead enemies stay in scene but are removed from active list
            for (let i = this.enemies.length - 1; i >= 0; i--) {
                const enemy = this.enemies[i];
                
                if (enemy.isDead) {
                    // Update death animation but keep in scene
                    enemy.update(deltaTime);
                    
                    // Remove from active enemies array after death animation completes
                    const timeSinceDeath = (Date.now() - enemy.deathTime) / 1000;
                    if (timeSinceDeath > 0.5) {
                        // Remove from enemies array but keep mesh in scene
                        this.enemies.splice(i, 1);
                    }
                } else {
                    enemy.update(deltaTime);
                }
            }
            
            // Update allies - use reverse loop to safely remove items
            for (let i = this.allies.length - 1; i >= 0; i--) {
                const ally = this.allies[i];
                
                if (ally.isDead) {
                    // Already removed from scene in die(), just remove from array
                    this.allies.splice(i, 1);
                } else {
                    ally.update(deltaTime);
                }
            }
            
            // Spawn new enemies if below minimum
            if (this.enemies.length < this.minEnemies && this.enemies.length < this.maxEnemies) {
                this.enemySpawnTimer += deltaTime;
                if (this.enemySpawnTimer >= this.enemySpawnInterval) {
                    this.spawnEnemies(1);
                    this.enemySpawnTimer = 0;
                }
            } else {
                this.enemySpawnTimer = 0;
            }
            
            // Update network
            if (this.networkManager) {
                this.networkManager.update(deltaTime);
            }
        }
        
            // Update blood particles
            this.bloodSystem.update(deltaTime);
            
            // Update confetti
            this.confettiSystem.update(deltaTime);
            
            // Always render (so you can see the scene during countdown)
            this.renderer.render(this.scene, this.camera);
    }
    
    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }
    
    destroy() {
        this.isRunning = false;
        
        // Disconnect network
        if (this.networkManager) {
            this.networkManager.disconnect();
        }
        
        // Cleanup
        if (this.player) {
            this.player.destroy();
        }
        
        this.enemies.forEach(enemy => {
            if (enemy.mesh) {
                this.scene.remove(enemy.mesh);
            }
            if (enemy.body) {
                this.world.removeBody(enemy.body);
            }
        });
        
        this.allies.forEach(ally => {
            if (ally.mesh) {
                this.scene.remove(ally.mesh);
            }
            if (ally.body) {
                this.world.removeBody(ally.body);
            }
        });
        
        // Cleanup confetti
        this.confettiSystem.destroy();
        
        audioManager.destroy();
        
        window.removeEventListener('resize', () => this.onWindowResize());
    }
}
